import {DifficultyLevelType} from './levels';
import {VoteType} from './vote';

declare global {
  type Question = TrackableObject & {
    contentType: 'question';
    id: number;
    uuid: string;
    slug: string;
    title: string;
    summary: string;
    body: string;
    type: string;
    level: DifficultyLevelType;
    normalized_level: number;
    date_added: string;
    date_asked: string;
    has_solution: boolean;
    has_hints: boolean;
    has_gotchas: boolean;
    has_test_cases: boolean;
    is_promoted: boolean;
    comment_placeholder: string;
    acceptance_rate: number;
    comment_count: number;
    upvotes: number;
    solution_upvotes: number;
    schema_model: string;
    companies: SeenCompany[];
    positions: SeenPosition[];
    tags: QuestionTag[];
    languages: QuestionLanguageOption[];
    course_info?: CourseQuestionInfo;
    challenge_questions: ChallengeQuestion[];
    content_quality: number;
    quality: number;
    bounce_rate: number;
    weight: number;
    vote: VoteType;
    solution_vote: VoteType;
    saved: boolean;
    completed: boolean;
    recommended_questions: Question[];
  };

  type MinimalQuestion = Pick<Question, 'uuid' | 'slug' | 'title' | 'summary' | 'tags' | 'level'>;

  type SeenCompany = {
    company: string;
    company_id: number;
    times_seen: number;
  };

  type SeenPosition = {
    position: string;
    position_id: number;
    times_seen: number;
  };

  type CourseQuestionInfo = {
    question_id: number;
    course_id: number;
    course_name: string;
    course_uuid: string;
    course_slug: string;
    course_icon: string;
    section_name: string;
    section_uuid: string;
    section_slug: string;
    learning_path_slug: string;
    total_sections: number;
    completed_sections: number;
  };

  type QuestionCategory = {
    id: number;
    name: string;
    locked: boolean;
    question_tags: QuestionTag[];
  };

  type QuestionPreview = {
    id: number;
    slug: string;
    title: string;
    body: string;
  };

  type QuestionListFilters = {
    types: string[];
    tags: string[];
    levels: DifficultyLevelType[];
    companies: string[];
    positions: string[];
    extraFilters: QuestionListExtraFilter[];
  };

  type QuestionListConfig = QuestionListFilters & {
    searchQuery: string;
    searchQuestionTag: string;
    searchCompany: string;
    ordering: QuestionListOrdering;
    orderingDirection: QuestionListOrderingDirection;
    pageSize: number;
    page: number;
  };

  type QuestionStats = {
    submission_stats_by_experience_level: SubmissionStatsByExperienceLevel;
    language_trendiness: LanguageTrendiness[];
  };

  type SubmissionStatsByExperienceLevel = Map<ExperienceLevel, QuestionSubmissionsStatsAggregation>;

  type QuestionSubmissionsStatsAggregation = {
    accepted: number;
    rejected: number;
    avg_attempts: number;
    acceptance_rate: number;
  };

  type LanguageTrendiness = {
    language: string;
    weight: number;
  };
}

export const enum ExperienceLevel {
  All = '0',
  Entry = '1',
  Student = '2',
  MidLevel = '3',
  Senior = '4',
}

export const enum QuestionListExtraFilter {
  Saved = 'Saved',
  Completed = 'Completed',
  WithSolutions = 'WithSolutions',
  WithoutSolutions = 'WithoutSolutions',
  Recent = 'Recent',
  Popular = 'Popular',
}

export const enum QuestionListOrdering {
  Recommended = 'Recommended',
  Title = 'Title',
  Score = 'Score',
  SuccessRate = 'SuccessRate',
  Difficulty = 'Difficulty',
}

export const enum QuestionListOrderingDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}
